import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { NavItem } from '@qpr/components/Expandable/nav-items-view-model';
import VerticalScroll from '@qpr/components/ScrollBar/VerticalScroll';
import React from 'react';
import MenuContent from './MenuContent';
import MenuHeader from './MenuHeader';

const PREFIX = 'MegaMenuDropdown';

const classes = {
  popperContainer: `${PREFIX}-popperContainer`,
  popperPaper: `${PREFIX}-popperPaper`,
  descriptionStyle: `${PREFIX}-descriptionStyle`,
  contentBox: `${PREFIX}-contentBox`,
  card: `${PREFIX}-card`,
  cardcontent: `${PREFIX}-cardcontent`,
  divider: `${PREFIX}-divider`,
  descriptionDiv: `${PREFIX}-descriptionDiv`,
  contentScrollbar: `${PREFIX}-contentScrollbar`
};

const StyledPopper = styled(Popper)(({ theme }) => ({
  [`&.${classes.popperContainer}`]: {
    width: '100%',
    zIndex: 10,
    transform: 'translate3d(0px, 64px, 0px) !important'
  },

  [`& .${classes.popperPaper}`]: {
    height: '100%',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: '8px',
    marginTop: '1px',
    background: theme.palette.primary.main
  },

  [`& .${classes.descriptionStyle}`]: {
    color: 'white'
  },

  [`& .${classes.contentBox}`]: {
    paddingLeft: '8px',
    height: '100%'
  },

  [`& .${classes.card}`]: {
    margin: '0',
    padding: '0',
    boxShadow: 'none',
    background: theme.palette.primary.main,
    color: 'white'
  },

  [`& .${classes.cardcontent}`]: {
    background: theme.palette.primary.main,
    padding: '0px',
    maxHeight: '400px'
  },

  [`& .${classes.divider}`]: {
    background: theme.palette.grey[300]
  },

  [`& .${classes.descriptionDiv}`]: {
    minHeight: '72px',
    paddingTop: '8px',
    paddingBottom: '16px'
  },

  [`& .${classes.contentScrollbar}`]: {
    marginTop: '4px',
    marginBottom: '4px'
  }
}));

export interface DropdownProps {
  anchorEl: Element | null;
  handleMenuClose: () => void;
  handleMenuOpen: (event: React.MouseEvent) => void;
  dropdownContentId: string | false;
  tenantSymbol: string;
  megaData: NavItem[];
  selectedTitle: string;
  selectedDescription: string;
}

export default function Dropdown({
  anchorEl,
  handleMenuClose,
  handleMenuOpen,
  dropdownContentId,
  tenantSymbol,
  megaData,
  selectedTitle,
  selectedDescription
}: DropdownProps) {
  const [itemDescription, setItemDescription] = React.useState<string | undefined>(
    selectedDescription
  );

  const createSecondLevel = (data: NavItem | undefined) => {
    if (data) {
      const result: Array<JSX.Element> = data.children.map((item: NavItem, idx: number) => {
        const menuSection = (
          <Grid key={item._id ? item._id : idx} item xs data-testid="megamenu-populated-column">
            <Box borderLeft={idx ? 1 : 0} borderColor="grey.300" className={classes.contentBox}>
              <Card className={classes.card}>
                <MenuHeader
                  key={item._id}
                  item={item}
                  tenantSymbol={tenantSymbol}
                  setItemDescription={setItemDescription}
                  onClose={handleMenuClose}
                />
                <VerticalScroll contentClassName={classes.contentScrollbar}>
                  <CardContent className={classes.cardcontent}>
                    <List>
                      {item.children &&
                        item.children.map((row: NavItem, index) => (
                          <MenuContent
                            typographyVariant="body1"
                            key={row._id ? row._id : index}
                            item={row}
                            setItemDescription={setItemDescription}
                            tenantSymbol={tenantSymbol}
                            onClose={handleMenuClose}
                          />
                        ))}
                    </List>
                  </CardContent>
                </VerticalScroll>
              </Card>
            </Box>
          </Grid>
        );
        return menuSection;
      });
      while (result.length < 3) {
        result.push(
          <Grid key={`emptysection${result.length}`} item xs data-testid="megamenu-empty-column">
            <Box borderLeft={1} borderColor="grey.300" className={classes.contentBox} />
          </Grid>
        );
      }
      return result;
    }
    return null;
  };

  const onMouseLeave = () => {
    setItemDescription('');
    handleMenuClose();
  };
  const onMouseEnter = (event: React.MouseEvent) => {
    setItemDescription('');
    handleMenuOpen(event);
  };

  return (
    <StyledPopper
      transition
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      placement="bottom"
      className={classes.popperContainer}
      id={`popper${dropdownContentId}`}
      data-testid="dropdown"
      // onResize={() => {}}
      // onResizeCapture={() => {}}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={{ enter: 300, exit: 250 }}>
          <div className={classes.popperPaper}>
            <Grid container spacing={1}>
              {createSecondLevel(
                megaData.find(
                  content => (content._id ? content._id.toString() : '') === dropdownContentId
                )
              )}
            </Grid>
            <Divider light className={classes.divider} />
            <div className={classes.descriptionDiv} data-testid="descriptionDiv">
              <Typography variant="subtitle1" className={classes.descriptionStyle}>
                {selectedTitle}
              </Typography>
              <Typography variant="body2" className={classes.descriptionStyle}>
                {itemDescription}
              </Typography>
            </div>
          </div>
        </Fade>
      )}
    </StyledPopper>
  );
}
